import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { IMessage } from '../interface/message';
import { StorageEnum } from '../enum/base';
import { MessageStatusEnum } from '../enum/common';
import { DEBOUNCE_SEEN_MESSAGE_DELAY } from '../constants/common';

const useVisibleMessages = (messages: IMessage[], containerRef: RefObject<HTMLDivElement>) => {
  const [visibleMessages, setVisibleMessages] = useState<number[]>([]);
  const accountId = localStorage.getItem(StorageEnum.ACCOUNT_ID);
  const debounceTimeoutRef = useRef<number | null>(null);

  const checkVisibility = useCallback(() => {
    const updatedVisibleMessages: number[] = [];
    const container = containerRef.current;

    if (container) {
      messages.forEach((message) => {
        if (message.senderAccountId !== accountId && message.status === MessageStatusEnum.DELIVERED) {
          const element = container.querySelector(`[data-message-id='${message.id}']`);
          if (element) {
            const rect = element.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            if (
              rect.top >= containerRect.top &&
              rect.bottom <= containerRect.bottom &&
              rect.left >= containerRect.left &&
              rect.right <= containerRect.right
            ) {
              updatedVisibleMessages.push(Number(message.id));
            }
          }
        }
      });
    }

    setVisibleMessages(updatedVisibleMessages);
  }, [messages, containerRef, accountId]);

  const handleDebouncedVisibilityCheck = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = window.setTimeout(() => {
      checkVisibility();
    }, DEBOUNCE_SEEN_MESSAGE_DELAY);
  }, [checkVisibility]);

  useEffect(() => {
    checkVisibility();

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleDebouncedVisibilityCheck);
      window.addEventListener('resize', handleDebouncedVisibilityCheck);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleDebouncedVisibilityCheck);
      }
      window.removeEventListener('resize', handleDebouncedVisibilityCheck);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [messages, containerRef, checkVisibility, handleDebouncedVisibilityCheck]);

  return visibleMessages;
};

export default useVisibleMessages;
